<template>
  <div class="!z-50">
    <ClientOnly>
      <component
        :is="IntlTelInput"
        v-if="IntlTelInput"
        ref="inputRef"
        v-model="inputValue"
        :options="{
          initialCountry: phoneDefaultCountry,
          utilsScript: true,
          strictMode: true,
          separateDialCode: true,
        }"
        :class="{ 'iti-error': isErrored, 'iti-disabled': isDisabled, 'iti__tel-input': !isErrored }"
        :disabled="isDisabled"
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import 'intl-tel-input/styles'
import { parsePhoneNumberWithError, isValidPhoneNumber } from 'libphonenumber-js'

const { t } = useI18n()
const phoneDefaultCountry = computed(() => (t('util.phoneDefaultCountry') || 'au').toLowerCase())

const IntlTelInput = shallowRef()
const inputRef = ref()
const inputValue = defineModel<string | null>('inputValue', {
  required: true,
})
const isErrored = defineModel<boolean>('isErrored', {
  required: false,
})
const isDisabled = defineModel<boolean>('isDisabled', {
  required: false,
})

const formattedInputNumber = ref<{
  rawInput: string
  number: string
  country: {
    name: string
    callingCode: string
    code: string
  }
}>()

const isValid = ref<boolean>(false)
const inputInstance = ref()

const populateInputWithData = (number: string) => {
  inputInstance.value = inputRef.value?.instance

  inputInstance.value.setNumber(number)
}

// format number on input paste or when populating from DB
watch (inputValue, (newValue) => {
  if (newValue && inputRef.value?.instance) {
    nextTick(() => {
      populateInputWithData(newValue)
    })
  }
}, { immediate: false })

const formatAndValidate = async (input: string) => {
  if (!input) {
    return isValid.value = false
  }

  inputInstance.value = inputRef.value?.instance

  const inputSelectedCode = inputInstance.value.getSelectedCountryData().iso2.toUpperCase()

  const parsedPhone = parsePhoneNumberWithError(input, inputSelectedCode)

  if (parsedPhone) {
    isValid.value = isValidPhoneNumber(parsedPhone.number, inputSelectedCode)
  }

  formattedInputNumber.value = {
    rawInput: input,
    number: parsedPhone.number,
    country: {
      name: inputInstance.value.getSelectedCountryData().name,
      callingCode: inputInstance.value.getSelectedCountryData().dialCodePlus,
      code: inputSelectedCode,
    },
  }
}

onMounted(async () => {
  IntlTelInput.value = (await import('intl-tel-input/vueWithUtils')).default
})

defineExpose({ formatAndValidate, isValid, formattedInputNumber, inputInstance, populateInputWithData })
</script>
